import * as THREE from 'three';
import { BONE, basic, cloth, emis, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import type { Rng } from '../rand.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/* ---------- carved pumpkins (the season's creature, the dressings and the decorations share them) ---------- */

const RIBS = 9;
const SQUASH = 0.78;

/** Radius factor of a ribbed pumpkin at azimuth `th` (0 = +Z, the face sits on a lobe). */
const lobe = (th: number): number => 0.9 + 0.1 * Math.sqrt(Math.abs(Math.cos((RIBS / 2) * th)));

/** A ribbed, squat pumpkin of radius 1 (height 2 × 0.78), dimpled at the stem and the base. */
export const pumpkinGeo = (w = 18, h = 12): THREE.BufferGeometry => {
  const geo = new THREE.SphereGeometry(1, w, h);
  const p = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const f = lobe(Math.atan2(x, z));
    const rxz = Math.hypot(x, z);
    const yy = y * SQUASH - Math.sign(y) * 0.14 * (1 - rxz) * (1 - rxz);
    p.setXYZ(i, x * f, yy, z * f);
  }
  geo.computeVertexNormals();
  return geo;
};

/** Front surface of the unit pumpkin at (x, y), a hair outside it. */
const surfaceZ = (x: number, y: number): number => {
  const z0 = Math.sqrt(Math.max(0.02, 1 - x * x - (y / SQUASH) * (y / SQUASH)));
  return z0 * lobe(Math.atan2(x, z0)) + 0.035;
};

/** Flat shapes laid onto the pumpkin's front: the carved face, lit from inside. */
const project = (shapes: THREE.Shape[]): THREE.BufferGeometry => {
  const geo = new THREE.ShapeGeometry(shapes, 2);
  const p = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) p.setZ(i, surfaceZ(p.getX(i), p.getY(i)));
  geo.computeVertexNormals();
  return geo;
};

const tri = (pts: [number, number][]): THREE.Shape => {
  const s = new THREE.Shape();
  pts.forEach(([x, y], i) => (i === 0 ? s.moveTo(x, y) : s.lineTo(x, y)));
  s.closePath();
  return s;
};

/** Carved faces: 0 grinning, 1 snarling (slanted brows, fangs), 2 screaming. Unit-pumpkin space. */
export const faceGeo = (style: 0 | 1 | 2 = 0): THREE.BufferGeometry => {
  const shapes: THREE.Shape[] = [];
  if (style === 1) {
    for (const k of [-1, 1]) shapes.push(tri([[k * 0.12, 0.22], [k * 0.52, 0.36], [k * 0.36, 0.08]]));
    shapes.push(tri([[0, 0.12], [-0.08, -0.02], [0.08, -0.02]]));
    const m = new THREE.Shape();
    m.moveTo(-0.6, -0.08);
    const teeth = 5;
    for (let i = 0; i <= teeth; i++) {
      const x = -0.6 + (1.2 * i) / teeth;
      m.lineTo(x, i % 2 ? -0.22 : -0.1);
    }
    m.lineTo(0.48, -0.4);
    for (let i = teeth; i >= 0; i--) m.lineTo(-0.48 + (0.96 * i) / teeth, i % 2 ? -0.3 : -0.46);
    m.closePath();
    shapes.push(m);
  } else if (style === 2) {
    for (const k of [-1, 1]) shapes.push(tri([[k * 0.1, 0.12], [k * 0.3, 0.42], [k * 0.46, 0.1]]));
    const m = new THREE.Shape();
    m.absellipse(0, -0.26, 0.2, 0.26, 0, Math.PI * 2, false, 0);
    shapes.push(m);
  } else {
    for (const k of [-1, 1]) shapes.push(tri([[k * 0.1, 0.1], [k * 0.3, 0.38], [k * 0.48, 0.1]]));
    shapes.push(tri([[0, 0.06], [-0.07, -0.06], [0.07, -0.06]]));
    const m = new THREE.Shape();
    m.moveTo(-0.62, -0.06);
    m.quadraticCurveTo(0, -0.34, 0.62, -0.06);
    m.lineTo(0.46, -0.3);
    m.lineTo(0.28, -0.3);
    m.lineTo(0.2, -0.2);
    m.lineTo(0.08, -0.36);
    m.lineTo(-0.08, -0.36);
    m.lineTo(-0.2, -0.2);
    m.lineTo(-0.28, -0.3);
    m.lineTo(-0.46, -0.3);
    m.closePath();
    shapes.push(m);
  }
  return project(shapes);
};

export const RIND = std('#d4651c', { roughness: 0.55, bumpScale: 0.01 });
export const RIND_DARK = std('#9c4310', { roughness: 0.6, bumpScale: 0.01 });
/** Vines and stems (one shared material keeps the dressed batches lean). */
export const VINE = std('#25301a', { roughness: 0.9 });
export const STEM = VINE;
/** A thin rag material for cloaks, hoods and capes (shared). */
export const RAG = cloth('#17131c', { side: THREE.DoubleSide });
/** The carved holes: candle-lit from inside. */
export const CARVED = emis('#ffd36a', '#ff8a1a', 2.2);

export interface Lantern {
  group: THREE.Group;
  glow: THREE.Sprite;
  /** Stop-motion candle flicker (call from the model's tick). */
  flick(rng: Rng): void;
}

/**
 * A jack-o'-lantern: ribbed rind, a crooked stem and a carved face glowing from within.
 * `r` is its radius; it faces +Z, its base at the group's origin.
 */
export const jackOLantern = (parent: THREE.Object3D, pos: THREE.Vector3, r: number, style: 0 | 1 | 2 = 0, rind: THREE.Material = RIND, glowSize = 3.4, stemMat: THREE.Material = STEM): Lantern => {
  const g = G(parent, pos.x, pos.y, pos.z);
  const body = G(g, 0, r * SQUASH, 0);
  body.scale.setScalar(r);
  M(pumpkinGeo(), rind, body);
  M(faceGeo(style), CARVED, body);
  const stem = M(new THREE.CylinderGeometry(0.06, 0.11, 0.34, 6), stemMat, body);
  stem.position.set(0.03, SQUASH + 0.08, 0);
  stem.rotation.z = -0.35;
  const glow = glowSprite(body, '#ff8a1a', glowSize, V(0, 0.02, 0.95), 0.75, true);
  return {
    group: g,
    glow,
    flick: (rng) => {
      glow.material.opacity = 0.5 + rng() * 0.45;
      // The sprite sits in the scaled body: its scale is in pumpkin radii.
      glow.scale.setScalar(glowSize * (0.85 + rng() * 0.3));
    },
  };
};

/* ---------- the cursed pumpkin (Halloween's creature) ---------- */

/**
 * Calabaza maldita: a lantern-headed thing. A carved jack-o'-lantern for a head, lit from inside; a body
 * of twisted vines and roots under a ragged black cloak; spindly vine limbs ending in twig claws.
 * Bursts when it dies (the view draws the blast). Bones: body at the hips (the cloak drifts on its own
 * pivot), the lantern head on its neck, arms bending at the elbow and legs at the knee.
 */
export const pumpkin = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 61);
  const { g, R, rng } = c;
  const vine = VINE;
  const root = std('#3a2c1d', { roughness: 0.95 });
  const rag = RAG;
  const ribs: Ribbon[] = [];
  const bodyJ = joint(g, V(0, 0.52, 0), 'body');
  const bd = bodyJ.abs;
  const cloakJ = joint(bd, V(0, 1.0, -0.04), 'cloak');
  const headJ = joint(bd, V(0, 1.0, 0.04), 'head');

  // Body: a knot of vines twisting up from the hips into the neck.
  for (let i = 0; i < 6; i++) {
    const a0 = (i / 6) * Math.PI * 2;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j <= 6; j++) {
      const t = j / 6;
      const a = a0 + t * 2.4;
      const rr = 0.13 * Math.sin(t * Math.PI) + 0.04;
      pts.push(V(Math.cos(a) * rr, 0.5 + t * 0.48, Math.sin(a) * rr * 0.8 + 0.02));
    }
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.028, 5), i % 2 ? vine : root, bd);
  }
  // A ragged cloak hung from the shoulders, torn into strips at the hem.
  const cg = new THREE.ConeGeometry(0.34, 0.78, 14, 4, true);
  const cp = cg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < cp.count; i++) {
    const y = cp.getY(i);
    if (y < -0.3) cp.setY(i, y + (i % 3 === 0 ? 0.16 : rng() * 0.1));
    // Open at the front: pull the front panel apart.
    if (cp.getZ(i) > 0.18 && y < 0.2) cp.setX(i, cp.getX(i) * 1.25);
  }
  cg.computeVertexNormals();
  const cloak = M(cg, rag, cloakJ.abs);
  cloak.position.set(0, 0.66, -0.04);
  cloak.rotation.x = -0.1;
  // A tattered collar of dead leaves around the neck.
  const leaf = RAG;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const l = M(new THREE.CircleGeometry(0.09, 5), leaf, bd);
    l.position.set(Math.cos(a) * 0.15, 1.0, Math.sin(a) * 0.13);
    l.rotation.set(-1.1, 0, a);
    l.scale.set(1, 1.6, 1);
  }

  // Legs: roots that split into toes, bending at the knee.
  const legJ = [-1, 1].map((k) => joint(g, V(k * 0.1, 0.52, 0), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.16, 0.26, 0.06), k < 0 ? 'shinL' : 'shinR'));
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const hip = V(k * 0.1, 0.52, 0);
    const at = (x: number, y: number, z: number): THREE.Vector3 => V(hip.x + x, hip.y + y, hip.z + z);
    limb(legJ[i]!.abs, at(0, 0, 0), at(k * 0.06, -0.26, 0.06), 0.035, 0.028, root, 5);
    limb(shinJ[i]!.abs, at(k * 0.06, -0.26, 0.06), at(k * 0.04, -0.5, -0.02), 0.028, 0.02, root, 5);
    for (let t = 0; t < 3; t++) limb(shinJ[i]!.abs, at(k * 0.04, -0.49, -0.02), at(k * 0.04 + (t - 1) * 0.07, -0.52, 0.1), 0.014, 0.006, root, 4);
  }
  // Arms: long thin vines hanging past the knees, twig claws; they bend at the elbow.
  const armJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  const foreJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  for (const k of [-1, 1]) {
    const sh = V(k * 0.17, 0.96, 0.02);
    const el = V(sh.x + k * 0.16, sh.y - 0.3, sh.z + 0.1);
    const wr = V(sh.x + k * 0.18, sh.y - 0.6, sh.z + 0.2);
    const aj = joint(bd, sh, k < 0 ? 'armL' : 'armR');
    const fj = joint(aj.abs, el, k < 0 ? 'foreL' : 'foreR');
    armJ.push(aj);
    foreJ.push(fj);
    limb(aj.abs, sh, el, 0.03, 0.022, vine, 5);
    limb(fj.abs, el, wr, 0.022, 0.016, vine, 5);
    for (let f = 0; f < 4; f++) {
      const tip = V(wr.x + (f - 1.5) * 0.04, wr.y - 0.14, wr.z + 0.08);
      limb(fj.abs, wr, tip, 0.01, 0.003, root, 4);
    }
    // A curling tendril off the forearm.
    const tp: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) tp.push(V(el.x + k * (0.04 + Math.sin(j) * 0.04), el.y - j * 0.04, el.z + Math.cos(j * 1.3) * 0.05));
    ribs.push(new Ribbon(fj.abs, tp, 0.008, vine, rng, 0.015));
  }

  // The head: a big carved lantern, a hair crooked on its neck.
  const head = G(headJ.abs, 0, 1.0, 0.04);
  const lan = jackOLantern(head, V(0, 0, 0), 0.25, 1, RIND, 3.6);
  head.rotation.z = 0.12;
  // Vines curling off the stem.
  for (let i = 0; i < 2; i++) {
    const pts: THREE.Vector3[] = [];
    const k = i ? 1 : -1;
    for (let j = 0; j < 6; j++) pts.push(V(k * (0.05 + j * 0.05), 0.42 + Math.sin(j * 1.4) * 0.05 - j * 0.02, -0.02 + Math.cos(j * 1.1) * 0.05));
    ribs.push(new Ribbon(head, pts, 0.01, vine, rng, 0.02));
  }
  // Inner fire seen through the carving, and a curl of smoke from the top.
  const core = glowSprite(head, '#ffb347', 0.4, V(0, 0.2, 0.05), 0.9, true);
  const smk = basic('#2a2230', 0.35);
  ribs.push(new Ribbon(head, [V(0.02, 0.44, 0), V(0.05, 0.56, -0.02), V(0.0, 0.7, -0.04), V(0.06, 0.86, -0.06)], 0.025, smk, rng, 0.03));
  // A few seeds clinging round the neck: the thing is full of them.
  for (let i = 0; i < 4; i++) {
    const s = M(new THREE.SphereGeometry(0.018, 5, 4), BONE, bd);
    s.scale.set(1, 0.5, 1.6);
    s.position.set(R() * 0.18, 0.94 + R() * 0.05, 0.12 + R() * 0.06);
  }
  // Faint ember motes drifting about it.
  const motes: THREE.Sprite[] = [];
  for (let i = 0; i < 3; i++) motes.push(glowSprite(g, '#ff7a1a', 0.07, V(0, 1, 0), 0.8, true));

  // Only the boil here (vines, candle, motes): the shamble is the clips' (anim/creatures/siege.ts).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    lan.flick(rng);
    core.material.opacity = 0.55 + rng() * 0.4;
    motes.forEach((m, i) => {
      const p = ((n * 0.13 + i / 3) % 1);
      m.position.set(Math.cos(i * 2.1 + n * 0.4) * 0.3, 0.6 + p * 0.9, Math.sin(i * 2.1 + n * 0.4) * 0.25);
      m.material.opacity = 0.9 * (1 - p);
    });
  };
  t(0);
  return done(c, 1.55, t, undefined, bonesOf(c, {
    body: bodyJ.p, cloak: cloakJ.p, head: headJ.p, armL: armJ[0]!.p, foreL: foreJ[0]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};
